import { useApolloClient } from "@apollo/client";
import { useQueueManifestDocumentExtraction } from "hooks/useQueueManifestDocumentExtraction";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback } from "react";

import { PROCESSED_MANIFEST_DOCUMENTS_QUERY, PROCESSED_MANIFESTS_QUERY } from "./api";

interface UseProcessManifestDocumentsArgs {
  tenantId: string;
  facilityId?: string;
  manifestTrackingNumber?: string;
  generatorId?: string;
  /** Open the manifest review dialog once extraction is ready for these docs. */
  onReviewReady: (documentIds: string[]) => void;
  /**
   * Fired when the extraction job reaches a terminal state. The drawer uses this
   * to poll for the data-source link (written by the downstream process-manifests
   * job, which finishes after extraction).
   */
  onProcessingComplete?: () => void;
}

/**
 * Run an already-uploaded document through the manifest document-intelligence
 * pipeline (queue extraction → minimized "analyzing" dialog → review handoff) —
 * the same path the upload dialog uses after creating the document, minus the
 * upload. Powers "Process manifest document data" / switching the data-source
 * document to a different PDF.
 */
export function useProcessManifestDocuments({
  tenantId,
  facilityId,
  manifestTrackingNumber,
  generatorId,
  onReviewReady,
  onProcessingComplete,
}: UseProcessManifestDocumentsArgs) {
  const { queueManifestDocumentExtraction } = useQueueManifestDocumentExtraction();
  const { createDialog } = useMinimizedDialogs();
  const client = useApolloClient();

  const launchAnalyzingDialog = useCallback(
    (jobId: string) => {
      const dialogId = `manifest-analyzing-${jobId}`;
      createDialog({
        id: dialogId,
        dialogType: DialogType.DocumentAIAnalyzing,
        props: {
          open: true,
          jobId,
          preferOriginalTitle: true,
          dialogId,
          wholeBoxClickable: true,
          onReview: (documentIds: string[]) => {
            if (documentIds.length > 0) onReviewReady(documentIds);
          },
          onComplete: () => {
            // Refresh the manifest row and the drawer's Documents section so the
            // processed document / new data source appears without a manual page
            // refresh (PROCESSED_MANIFEST_DOCUMENTS_QUERY backs that section).
            client.refetchQueries({
              include: [PROCESSED_MANIFESTS_QUERY, PROCESSED_MANIFEST_DOCUMENTS_QUERY],
            });
            // The data-source link is written by the downstream process-manifests
            // job, which runs after extraction — let the drawer poll for it.
            onProcessingComplete?.();
          },
        },
      });
    },
    [createDialog, client, onReviewReady, onProcessingComplete]
  );

  const processDocuments = useCallback(
    async (documentIds: string[]) => {
      if (!tenantId || documentIds.length === 0) return;
      const jobs = await queueManifestDocumentExtraction({
        tenantId,
        documentIds,
        facilityId,
        manifestTrackingNumber,
        generatorId,
      });
      const jobId = jobs[0]?.job.id;
      if (jobId) launchAnalyzingDialog(jobId);
    },
    [
      queueManifestDocumentExtraction,
      tenantId,
      facilityId,
      manifestTrackingNumber,
      generatorId,
      launchAnalyzingDialog,
    ]
  );

  return { processDocuments };
}
